import { format } from 'date-fns'
import map from 'lodash/map'
import { useRouter } from 'next/router'
import React, { FC } from 'react'

import SVGCheck from '@Assets/SVG/Check'
import SVGExclamation from '@Assets/SVG/Exclamation'
import SvgPhone2 from '@Assets/SVG/Phone2'
import { Title } from '@Components/CMS/Title'
import { SVGIcon } from '@Components/Icons/SVGIcon'
import { Button } from '@CoreComponents/Buttons/Button'
import { PageType, TrackingState, TrackingType, useTrackingDataQuery } from '@Generated/graphql'
import { useCMSModalOpen } from '@Globals/CMS/Modal/Hook'
import { useTranslation } from '@Globals/Translations'

import styles from './Tracking.module.scss'

interface TrackingProps {
  id?: string
}

interface TrackingEntity {
  date?: Date | null
  isComplete: boolean
  locations: string[]
  state?: TrackingState | null
  subtexts: string[]
  type?: TrackingType | null
}

export const Tracking: FC<TrackingProps> = ({ id }) => {
  const { t } = useTranslation('tracking')
  const router = useRouter()
  const openModal = useCMSModalOpen()

  const { data } = useTrackingDataQuery({
    variables: {
      token: router.query.trackingToken?.toString(),
      orderNumber: router.query.orderNumber?.toString(),
    },
    fetchPolicy: 'cache-only',
  })

  if (!data?.tracking) {
    const trackingErrorString = t(
      'IF_YOU_THINK_THIS_IS_A_MISTAKE_PLEASE_CONTACT_US_AND_WE_WILL_HELP_YOU',
      { arg1: '/info/contact' },
    )
    return (
      <div className={styles.trackingNotFound}>
        <Title title={t('THE_SHIPMENT_HAS_NO_TRACKING_INFORMATION_FROM_THE_LAST_30_DAYS')} type="h2" />
        <p dangerouslySetInnerHTML={{ __html: trackingErrorString }} />
      </div>
    )
  }

  const icons = {
    check: SVGCheck,
    exclamation: SVGExclamation,
  }

  const renderTrackingIconV2 = (state: TrackingState, completed: boolean) => {
    let icon: keyof typeof icons = 'check'
    if (state === TrackingState.Exception) {
      icon = 'exclamation'
    }

    return (
      <div className={`
      ${styles.trackingIcon} ${completed ? styles.fill : ''}
      ${state === TrackingState.Exception ? styles.exception : ''}
      ${state === TrackingState.Delivered ? styles.delivered : ''}`}
      >
        {completed ? <SVGIcon icon={icons[icon]} size="10px" /> : <div className={styles.trackingDot} />}
      </div>
    )
  }

  const getTransformedMessages = (messages: string[]) => messages.map(x => t(x, { TRENDHIM_WAREHOUSE: t('TRENDHIM_WAREHOUSE'), DENMARK: t('common:DENMARK') }))

  const getTrackingTitle = (state: TrackingState, type: TrackingType | null | undefined) => {
    let translatedState: string = state

    switch (state) {
      case TrackingState.Shipped:
        translatedState = 'DISPATCHED_FROM_TRENDHIM'
        break
      default:
    }

    if (type === TrackingType.Bulk) {
      switch (state) {
        case TrackingState.Delivered:
          translatedState = 'HANDED_OVER_TO_YOUR_LOCAL_DELIVERY_SERVICE'
          break
        case TrackingState.OutForDelivery:
          translatedState = 'IN_TRANSIT'
          break
        default:
      }
    }

    return t(translatedState)
  }

  let steps: TrackingEntity[] = []
  if (data.tracking.events?.length) {
    steps = data.tracking.events
      .map((x, index) => {
        const newItem: TrackingEntity = {
          state: x.state,
          date: x.timestamp,
          isComplete: x.state
            ? [TrackingState.OrderPlaced, TrackingState.Packed, TrackingState.Shipped, TrackingState.Delivered].includes(
              x.state,
            ) || index > 0
            : false,
          subtexts: x.messages ? [...getTransformedMessages(x.messages)] : [],
          locations: [],
          type: x.type ?? TrackingType.Unspecified,
        }

        if (x.subState && x.subState.includes('EXCEPTION_')) {
          const exceptionId = x.subState.split('_')[1]
          newItem.subtexts.push(t(`EXCEPTION_TEXT_${exceptionId}`))
        }

        if (x.location && !(x.type === TrackingType.Bulk && x.state === TrackingState.Delivered)) {
          newItem.locations.push(x.location)
        }

        return newItem
      })
      .filter(x => x.state !== TrackingState.InfoReceived)
  }

  return (
    <div className={styles.wrapper} id={id}>
      <div className={styles.overview}>
        <div className={styles.title}>{t('OVERVIEW_LABEL')}</div>
        <div className={styles.orderId}>
          <strong>
            {t('ORDER_ID_LABEL')}
            :
            {' '}
          </strong>
          <span>{data.tracking.orderID}</span>
        </div>
        <p>
          <SvgPhone2 />
          <button className={styles.button} onClick={() => { openModal(PageType.Modal, 'contact-us') }} type="button">
            {t('footer:FOOTER_CONTACT_TITLE')}
          </button>
        </p>
        {data.tracking?.trackingURL && (
          <div className={styles.trackingLink}>
            <Button
              innerSpacing="medium"
              onClick={() => window.open(data.tracking?.trackingURL?.toString(), '_blank')?.focus()}
              theme="black"
              type="button"
            >
              {t('CA_TRACKING_LINK')}
            </Button>
          </div>
        )}
      </div>
      <div className={styles.trackingInformation}>
        <div className={styles.trackingInformationTitle}>{t('ORDER_TRACKING_HISTORY')}</div>
        <div className={styles.trackingSteps}>
          <div className={styles.trackingStepsInner}>
            {map(steps, (step, index) => (
              <div
                key={`trackingitem_${index}`}
                className={`
              ${styles.trackingStepItem}
              ${step?.state === TrackingState.Delivered ? styles.delivered : ''}
              ${index === 0 ? styles.first : ''}`}
              >
                <div className={styles.trackingItem}>
                  {step.state && renderTrackingIconV2(step.state, step.isComplete)}
                  <div className={styles.trackingContent}>
                    {step.date && <div className={styles.trackingDate}>{format(new Date(step.date), 'LLL, d H:mm')}</div>}
                    <div className={styles.trackingTitle}>
                      {step.state && getTrackingTitle(step.state, step.type)}
                    </div>
                    {!!step.subtexts?.length && (
                      <div className={styles.trackingDescription}>
                        {map(step.subtexts, text => <div>{text}</div>)}
                      </div>
                    )}
                  </div>
                </div>
                {!!step.locations.length
                && map(step.locations, (location, subindex) => (
                  <div key={`trackingsubitem_${subindex}`} className={styles.trackingSubItem}>
                    <div className={styles.trackingDot} />
                    {location}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
